import { ColumnConfig } from '@/components/table/Table';
import { extractDate, formatDate } from '@/utils/dates';
import { Link } from 'react-router-dom';
import { isPickupServiceVisit } from '@/pages/Business/pages/LogisticOperator/models/ServiceWithOperator';
import DriverNoticesIndicator from '../components/DriverNoticesIndicator';

const formatAddress = (address: any): string => {
  if (!address) return '______';
  return [address.streetName, address.doorNumber, address.apartmentNumber]
    .filter(Boolean)
    .join(' ') || '______';
};

// Misma clasificacion que usa el formulario, para que la columna y el
// autollenado de direcciones no discrepen sobre que es un retiro.
const isPickupService = (rowData: any): boolean =>
  isPickupServiceVisit(rowData?.serviceWithOperator);

export const shippingsTableColumns: ColumnConfig[] = [
  {
    label: 'ID Pedido',
    field: 'number',
    sortable: true,
    render: (value: string | number, rowData: any) => {
      const shippingNumberText = String(value ?? '').trim();
      const shippingNumberAsNumber = Number(shippingNumberText);
      if (
        !shippingNumberText ||
        !Number.isFinite(shippingNumberAsNumber) ||
        shippingNumberAsNumber <= 0
      ) {
        return shippingNumberText || '______';
      }

      return (
        <span className='inline-flex items-center gap-1'>
          <Link
            to={`/business/shipping/${shippingNumberText}`}
            onClick={(event) => event.stopPropagation()}
            className='font-semibold text-[#1976d2] hover:text-[#1565c0] tabular-nums focus:outline-none focus-visible:ring-2 focus-visible:ring-[#1976d2] focus-visible:ring-offset-1 rounded'
          >
            {shippingNumberText}
          </Link>
          <DriverNoticesIndicator notices={rowData?.driverNotices} />
        </span>
      );
    },
  },
  {
    label: 'ID Externo',
    field: 'commerceShippingNumber',
    render: (value: string) => value || '______',
  },
  {
    label: 'Comercio',
    field: 'commerce',
    sortable: true,
    render: (value: any) => value?.name || '______',
  },
  {
    label: 'Servicio',
    field: 'commercialService',
    sortable: true,
    render: (value: any, rowData: any) => {
      const service = value?.name
        ? value
        : rowData?.serviceWithOperator?.commercialService;
      if (!service?.name) return '______';
      const color = service?.color || '#FFB800';
      return (
        <div
          className='px-3 py-1 rounded-md text-sm font-semibold inline-block'
          style={{
            backgroundColor: `${color}20`,
            color: color,
          }}
        >
          {service.name}
        </div>
      );
    },
  },
  {
    label: 'Zona',
    field: 'shippingZones',
    sortable: false,
    render: (value: any[]) => {
      if (!value || value.length === 0) return '______';
      return (
        value
          .map((sz: any) => sz.zone?.name)
          .filter(Boolean)
          .join(', ') || '______'
      );
    },
  },
  {
    label: 'Nombre del cliente',
    field: 'recipient',
    sortable: true,
    render: (value: string) => value || '______',
  },
  {
    label: 'Dirección de entrega',
    field: 'destinationAddress',
    sortable: false,
    render: (value: any, rowData: any) =>
      formatAddress(isPickupService(rowData) ? rowData?.originAddress : value),
  },
  {
    label: 'Fechas',
    field: 'createdAt',
    sortable: false,
    render: (_value: string, rowData: any) => {
      // ETA es DATEONLY en la base (sin hora); el resto son timestamps reales.
      const allDates = [
        { color: 'bg-[#9e9e9e]', label: 'Ingreso',   value: rowData?.createdAt,     withTime: true },
        { color: 'bg-[#2196f3]', label: 'ETA',        value: rowData?.dateToDelivery, withTime: false },
        { color: 'bg-[#ffc107]', label: 'Auditoría',  value: rowData?.entryDate,      withTime: true },
        { color: 'bg-[#4caf50]', label: 'Entregado',  value: rowData?.dateDelivered,  withTime: true },
      ].filter((d) => !!d.value);

      if (allDates.length === 0) return null;

      const is4 = allDates.length === 4;

      return (
        <div className={is4 ? 'grid grid-cols-2 gap-1 w-fit' : 'flex gap-1'}>
          {allDates.map(({ color, label, value, withTime }) => (
            <div
              key={label}
              className={`${color} text-white text-xs font-semibold px-2 py-1 rounded-sm flex flex-col items-center leading-tight min-w-[85px]`}
            >
              <span className='text-[9px] font-normal opacity-90'>{label}</span>
              <span className='whitespace-nowrap'>
                {withTime ? formatDate(value) : extractDate(value)}
              </span>
            </div>
          ))}
        </div>
      );
    },
  },
  {
    label: 'Conductor',
    field: 'lastDriverName',
    render: (value: string) => value || '-',
  },
  {
    label: 'Estado',
    field: 'status',
    render: (value: any) => value?.commercialName || '______',
  },
];
